// The factory layer: scrap heaps, belt parts, and the cargo riding them.
//
// This is where 3D earns its cost (PLAN §5.7.3): cargo is not a counter, it is
// a box that moves one square a turn; grade is not a pip, it is the size and
// colour of that box; a heap is not a number, it is a pile that shrinks as it
// is mined.
//
// Belt direction was meant to work the same way — "a conveyor you can see
// which way runs" — but the asset cannot deliver it. Kenney's conveyor.glb is
// mirror-symmetric along its own run (verified: all 144 vertices mirror on
// both X and Z), so a part turned 180 degrees is pixel-identical and flow
// direction is literally unrenderable from the mesh. It is the pawn problem
// from PLAN §5.7.4 again, and it takes the same answer: a procedural chevron
// on top, sharing the pawn's shape so "chevron means facing" stays one idea.

import {
  BoxGeometry,
  type BufferGeometry,
  Color,
  InstancedMesh,
  type Material,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Quaternion,
  Vector3,
  type Texture,
  AdditiveBlending,
  CylinderGeometry,
  MeshBasicMaterial,
  PlaneGeometry
} from "three";

// The contract, and nothing else, says what is on the board (PLAN-CLIENT-SPLIT
// days 3–4). The engine's *types and word accessors* are still imported — a
// pile is sized and coloured by the packed unit word `factNames.unitWord`
// makes, and the meshes are keyed by the numbers the rules used — but no raw
// state is read and no rules accessor is called on a board.
import {
  type BeltGhost as ApiBeltGhost,
  type BoardFacts,
  type BuildingName,
  type CargoLeg,
  fileOf,
  rankOf,
  type Square,
  type SquareFacts,
  type StructureName,
  type UnitFacts
} from "src/api";
import type { Direction, PieceType } from "src/shared/board";
import { type StructureKind, unitGrade, unitLine } from "src/shared/industry";
import { TurretFire } from "./turretFire";
import { builtBelt, DIR_INDEX, LINE_INDEX, PIECE_TYPE, unitWord } from "./factNames";

import {
  type BeltAnim,
  deckScale,
  nearestAngle,
  newBeltAnim,
  setLoaded,
  SHARED_SQUASH,
  stepBeltAnim
} from "./beltAnim";
import { bendBetween, type Corner } from "./beltCorners";
import { fadeBelowBoard, setTransparent } from "./riseFade";
import { createChevronGeometry } from "./chevron";
import { createOreLumpGeometry } from "./oreLump";
import { bendConveyor, mirrorConveyor, type Hand } from "./curvedBelt";
import { FACING_YAW, squareToWorld } from "./coords";
import { CargoMotion, type IngotSpot, type Phase, spotKey } from "./cargoMotion";
import {
  INGOT_CENTRE_Z,
  INGOT_EXTENT,
  type KenneyModel,
  type KenneyName,
  type PackBelts,
  type PieceGeometries,
  type PropGeometries
} from "./models";
import { Outlines } from "./outline";
import { armyColor, PALETTE } from "./palette";
import { buildStructureGhost } from "./structureGhost";

/**
 * Grade reads as size and colour: bigger and brighter is better material.
 *
 * Grade 0 is the heap's own rust, not a neutral grey — a unit of scrap riding
 * a belt is the same stuff as the pile it was mined from, and the grey read as
 * scenery against an orange part rather than as the ore you are watching move.
 */
/** Iron, steel, crucible steel, electrum, diamond — see `PALETTE.grades`. */
const GRADE_COLORS = PALETTE.grades;
const GRADE_SCALE = [0.34, 0.4, 0.46, 0.49, 0.52];

/**
 * What one unit looks like — **the one place a drawn unit's colour is decided**.
 *
 * A pile used to be a list of *grades*, so every line drew in the metal
 * palette: cobble hauled across the board was iron, and the only place stone
 * looked like stone was the field it came out of. The lists are unit words now
 * and the line is read off the word here, which is what makes held metal,
 * loose metal and belt cargo agree without three copies of the table.
 */
export function unitColour(unit: number): number {
  const grade = unitGrade(unit);
  const table =
    unitLine(unit) === LINE_INDEX.stone
      ? PALETTE.stoneGrades
      : unitLine(unit) === LINE_INDEX.special
        ? PALETTE.specialGrades
        : unitLine(unit) === LINE_INDEX.fuel
          ? PALETTE.fuelGrades
          : GRADE_COLORS;
  return table[grade] ?? table[0] ?? GRADE_COLORS[0];
}

/** How big one unit draws. Size is the *rung*, which every line shares. */
const unitScale = (unit: number): number =>
  GRADE_SCALE[unitGrade(unit)] ?? GRADE_SCALE[0];

/**
 * The units on a square that are lying `where`, as the words a pile is drawn
 * from, in pile order.
 *
 * The engine hands `units` cheapest first, by value rather than by rung, and
 * stable — a pile builds from the bottom, so the cheap metal underneath means
 * the valuable thing is the one you see, and the order must not reshuffle when
 * a unit arrives. Filtering keeps that order, so nothing is sorted here.
 *
 * A piece's holdings are `held` *and* `cooking` — a king refines what it digs,
 * so counting the banks alone loses material mid-cook.
 */
const wordsWhere = (square: SquareFacts, ...where: readonly UnitFacts["where"][]): number[] =>
  square.units.filter((unit) => where.includes(unit.where)).map(unitWord);

/**
 * Where each ingot of a pile sits, and which way it is turned.
 *
 * **Derived from the mesh, not tuned against it.** The old table was written in
 * "units of one ingot" and the units were wrong: the layer step was 0.62 where
 * the bar is 0.2629 tall, so every stack floated with a third of an ingot of
 * air under each layer, and the in-plane steps left the same gap to the side. A
 * pile read as a loose scatter rather than as metal stacked up.
 *
 * The lattice now comes from `INGOT_EXTENT`:
 *
 *  - **Two bars to a layer**, side by side across their depth. A bar is 1.0
 *    long and 0.3865 deep, so two side by side is very nearly square and three
 *    would overhang the tile at the top grade.
 *  - **Layers sit flush.** The step is exactly the bar's height, and each
 *    layer's height is the height of the *largest* grade in it, so a mixed pile
 *    has no gaps and nothing intersects.
 *  - **Each layer is turned `PILE_TWIST` further round**, as a stack of bars
 *    dropped in courses is. The whole layer turns, offsets and all, so the two
 *    bars in it stay side by side and never cross.
 *
 * Grades are sorted cheapest-first by the callers, so the pile builds with the
 * valuable metal on top and the order is stable.
 */
const PILE_PER_LAYER = 2;
/** How far each course is turned from the one below it. */
const PILE_TWIST = Math.PI / 6;

/** How many ingots a pile draws before it stops adding and starts counting. */
const MAX_PILE = 8;

export interface IngotSlot {
  /** Offsets from the pile's centre, in world units. */
  x: number;
  y: number;
  z: number;
  /** Which way the bar points, in radians about Y. */
  yaw: number;
  /** The scale this slot was laid out for — its own grade's. */
  size: number;
}

/**
 * The slots for a pile, given each ingot's drawn size in bottom-up order.
 *
 * Sizes rather than a count, because grade decides how big a bar is and a
 * layer's height is the tallest thing in it. A layout that assumed one size
 * would either bury the small ingots or float the large ones.
 */
export function pileSlots(sizes: readonly number[]): IngotSlot[] {
  const out: IngotSlot[] = [];
  let base = 0;

  for (let start = 0; start < Math.min(MAX_PILE, sizes.length); start += PILE_PER_LAYER) {
    const layer = sizes.slice(start, start + PILE_PER_LAYER);
    // The tallest bar in the course decides the course's height, so nothing in
    // it is buried and the one above it still rests on something.
    const tallest = Math.max(...layer);
    const yaw = (start / PILE_PER_LAYER) * PILE_TWIST;
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);

    layer.forEach((size, slot) => {
      // Side by side across the bar's depth, centred on the pile.
      const across = (slot - (layer.length - 1) / 2) * INGOT_EXTENT.z * tallest;
      // The mesh's origin is at its front edge, so undo that before turning —
      // otherwise every bar orbits a point half its own depth behind itself.
      const offZ = across - INGOT_CENTRE_Z * size;
      out.push({
        // The whole course turns, which is what keeps two bars side by side
        // side by side rather than crossing.
        x: -offZ * sin,
        y: base + (INGOT_EXTENT.y * size) / 2,
        z: offZ * cos,
        yaw,
        size
      });
    });

    base += INGOT_EXTENT.y * tallest;
  }
  return out;
}

/**
 * One part of a run that is being drawn but has not been laid yet.
 *
 * Drawn as the belt it will become rather than as a marker on the tile: a lane
 * is a *direction* and a shape, and a wash on the ground says neither. Until
 * this existed, the way to find out which way a drawn run pointed and where it
 * bent was to lay it and look — and the fix for a wrong answer was to take the
 * whole thing up again.
 */
export interface BeltGhost {
  square: number;
  direction: Direction;
  /**
   * The direction material arrives by, when the part before this one in the
   * run feeds it. What tells the preview it is drawing a bend.
   */
  inbound?: Direction;
  /** A part of yours is already here, and laying would only turn it. */
  repoint: boolean;
}

/**
 * Every drawn part on the board, as the ghosts of the belts they will become.
 *
 * A blueprint has no cargo, no owner colour on the deck and no switch — there
 * is nothing to draw but the shape and the direction, which is exactly what
 * the preview already draws. Bends come from the *network* rather than from a
 * run, because a stored blueprint has no memory of the gesture that made it:
 * the engine's `belt.bend` counts the parts feeding it, drawn or built, so a
 * run grown off the end of a working lane turns where it will turn once it is
 * paid for.
 *
 * In square order, like every other sweep here — two clients drawing one board
 * in a different order is a difference nothing else would ever report.
 */
function blueprintGhosts(facts: BoardFacts): BeltGhost[] {
  const out: BeltGhost[] = [];
  for (const [square, { belt }] of facts.squares) {
    if (!belt?.blueprint) continue;
    out.push({
      square,
      direction: DIR_INDEX[belt.dir],
      inbound: cornerOf(belt)?.inbound,
      // Nothing to hide: the deck under a blueprint is the blueprint.
      repoint: false
    });
  }
  return out;
}

/**
 * The corner a part is drawn as, or null where it runs straight.
 *
 * The engine decides the bend: one part feeding off the axis is a corner, two
 * is a *merge*, and a merge is not a bend — there is no single arc that serves
 * two entries, and drawing one would pick a winner between two lanes that are
 * equally real. Those stay straight, and the cargo piling up on the square is
 * what shows the join. The arc starts at the end material *arrives* by, which
 * for a corner is its one feeder.
 */
function cornerOf(belt: NonNullable<SquareFacts["belt"]>): Corner | null {
  if (belt.bend !== "left" && belt.bend !== "right") return null;
  return { inbound: DIR_INDEX[belt.feeders[0]], hand: belt.bend };
}

/**
 * "No squares", shared and frozen.
 *
 * The pass watcher answers on every tick and almost every pass has no ovens in
 * it, so this is the ordinary case — and a caller must not be able to hold the
 * array and write to it.
 */
const NO_SQUARES: readonly number[] = Object.freeze([]);

const MAX_BELTS = 64;
/**
 * Cargo and holdings are budgeted per *ingot*, not per square, because a pile
 * is drawn as its individual bars — so one loaded square can cost eight of
 * these. The board also grows now, so 64 was a ceiling the game could reach.
 */
const MAX_CARGO = 64;
/** Starting capacity only — every pool below grows by doubling (`fit`). */
/** Wrecks on the board at once, per piece type. */
const MAX_WRECKS = 16;
/** Every type a wreck can be. A king's wreck happens when an army is beaten. */
const WRECK_TYPES: PieceType[] = [
  PIECE_TYPE.pawn,
  PIECE_TYPE.knight,
  PIECE_TYPE.bishop,
  PIECE_TYPE.rook,
  PIECE_TYPE.queen,
  PIECE_TYPE.king
];
/**
 * How far a wreck and the ore sit either side of the square's centre.
 *
 * They share the square, and the wreck used to be a cone in the middle of it
 * with cargo and trinkets drawn on top. Offsetting both means a square that
 * holds scrap *and* ore reads as two things rather than one pile with
 * something buried in it.
 */
const WRECK_OFFSET = 0.22;
/** A wreck is a fraction of a living piece: it is not still playing. */
const WRECK_SCALE = 0.42;
/**
 * The costume a disguised piece is wearing, drawn beside its own holdings.
 *
 * **A token, not a tint.** Blending the wearer's colours toward the army it is
 * impersonating was the first answer and it says the wrong thing twice: a
 * half-and-half piece reads as a piece that is *partly* somebody's, which is
 * not what a disguise is, and it would collide with `ArmyTint` — the one
 * mechanism that says whose a piece is, on a board where that is the whole
 * question. So the wearer is drawn exactly as it always was and the costume is
 * a separate small object standing next to it, which is also the honest
 * picture: this piece, plus a claim about what it looks like to somebody else.
 *
 * It reuses the wreck geometry, since that is already a small piece model per
 * rank in a pool that grows — but **upright**, because it is a costume rather
 * than a corpse, and on the opposite side of the square from the wreck, so a
 * square holding both says both.
 */
const TOKEN_OFFSET = 0.26;
const TOKEN_SCALE = 0.34;
/** Clear of the deck, so it reads as held up rather than standing on the board. */
const TOKEN_LIFT = 0.18;
/** A wreck falls across the rank, away from the ingots beside it. */
const FALLEN_AXIS = new Vector3(1, 0, 0);
/** Scratch, so laying a wreck down allocates nothing per frame. */
const FALLEN = new Quaternion();

/**
 * A stable number in [0, 1) for a square.
 *
 * A hash rather than a roll: it has to be the same every sync, or the pile
 * twitches whenever anything on the board changes, and the same on two
 * clients, or one board is two pictures. Cheap integer mixing — this runs per
 * wreck per sync and the only property it needs is that neighbouring squares
 * do not come out alike.
 */
function scatter(square: number): number {
  let x = Math.imul(square ^ 0x9e3779b9, 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}
/** Loose things turn about +Y, the board's up. */
const SPIN_AXIS = new Vector3(0, 1, 0);
const MAX_HOLDINGS = 64;
/** Buildings on the board at once. Starting capacity; the pool grows. */
const MAX_STORES = 16;
/**
 * How many beams may be on screen at once.
 *
 * Every turret on the board can be mid-shot together — the volley overlaps by
 * design — and a turret may hit several targets a firing, so this is stores
 * times a generous targets-per-gun rather than a number picked by eye. Grown
 * like every other pool here if it is ever reached; never truncated, which is
 * the failure mode a fixed ceiling has (a draw loop that silently stops).
 */
const MAX_BEAMS = MAX_STORES * 8;
/** Thin enough to read as a beam rather than as a pipe between two squares. */
const BEAM_RADIUS = 0.04;
/** Where a beam leaves the gun and where it lands, in board heights. */
const BEAM_FROM = 0.42;
const BEAM_TO = 0.5;
/**
 * A building's plinth: the square it stands on, which is no longer a square.
 *
 * This is what stops a store reading as "a lot of material". The codebase
 * holds one idea — a box is material — so a big crate on bare ground would
 * say exactly the wrong thing about a warehouse. The pad is flat, square,
 * owner-coloured and obviously *built*, and the crate sits on it.
 */
const PAD_HEIGHT = 0.09;
const PAD_INSET = 0.96;
/**
 * The crate on top: inset so the pad shows as a rim around it, and taller
 * than it is wide-scaled so the building has presence from the side. Still
 * shorter than a pawn — it is a warehouse, not a piece.
 */
const CRATE_SCALE = 0.78;
const CRATE_HEIGHT = 1.35;

/**
 * The §31 S4–S6 kinds draw as the store's crate, stretched — width and height
 * multipliers — until they have models of their own (client ask): a wall low
 * and wide, a generator tall, a cabling tower a post.
 */
const BUILDING_SHAPE: Partial<Record<BuildingName, readonly [number, number]>> = {
  "simple-wall": [1.15, 0.55],
  "firm-wall": [1.15, 0.75],
  "reinforced-wall": [1.2, 1.05],
  armory: [1, 1.25],
  "coal-generator": [0.85, 1.5],
  "nuclear-generator": [0.85, 1.8],
  battery: [0.65, 1.2],
  "cabling-tower": [0.35, 2],
  // §31 S7: track lies flat; a train is a low long box on it.
  track: [1.2, 0.08],
  "train-engine": [0.95, 0.9],
  "train-car": [0.95, 0.6]
};

const TILE_TOP = 0.001;

/**
 * An ore field, drawn twice over (§26 F3).
 *
 * The **wash** is terrain: a faint tint on the deck of every tile of a field,
 * saying which squares are one deposit and what it is made of. Deliberately
 * far fainter than any highlight — a marker means *something may happen here*
 * and this means *this is what the ground is*, and a field that read as loudly
 * as a legal move would drown the board it lies under.
 *
 * The **lumps** are the charge: a rock per sixth of a fresh tile left, so a
 * field that is being worked visibly thins out from wherever the digging is
 * happening. That is the whole reason the count is drawn rather than a number
 * — depletion draws itself, which a rock a unit stopped doing the moment a
 * tile held more units than the cap and sat full until it was nearly dry.
 *
 * Not a `HighlightKind`, though the colour-belongs-to-the-kind rule is the
 * same one. A highlight is transient and pushed from the interface; this is
 * state, synced from the square's facts beside heaps and cargo, and every
 * highlight kind draws a *marker* on top of its wash, which terrain must not.
 */
const ORE_WASH_Y = 0.004;
const ORE_WASH_OPACITY = 0.16;
const ORE_LUMP_Y = 0.02;
const ORE_LUMP_SCALE = 0.2;
/** How far off centre a lump may sit, so a tile reads as scattered rock. */
const ORE_LUMP_SPREAD = 0.3;
/** Rocks on one tile. Past this the tile is a mound and the count stops
 *  reading, and a rich seam tile would cost more instances than a lane. */
const MAX_LUMPS = 6;
/** Rocks drawn on a tile `fraction` (0..1) as full as a fresh one — the fact's
 *  own measure, so a full tile is exactly a full mound and the seed dial cannot
 *  drift from the picture. One answer, read by the draw loop and by the pool
 *  that has to be big enough for it. */
export const lumpsFor = (fraction: number): number =>
  Math.min(MAX_LUMPS, Math.ceil(fraction * MAX_LUMPS));
/** Starting capacity for both ore pools; they grow like every other. */
const MAX_ORE_TILES = 64;
/** Top surface of a running conveyor, where its direction chevron sits. */
export const BELT_DECK = 0.41;
const ONE = new Vector3(1, 1, 1);
/** No rotation. A building is square to the board; nothing about it turns. */
const NO_TURN = new Quaternion();
/**
 * Which way the turret model's barrel points in its own file.
 *
 * **A fact about the asset, not about the game**, which is why it lives here
 * and not in `aimTurret`. That function derives its yaw from the world's own
 * axes — file runs +X, rank runs −Z — and is right; the model simply is not
 * built facing local −Z the way the aim assumes, so every gun drew a quarter
 * turn anticlockwise of its target. Reported from play.
 *
 * Corrected at the one place the aim becomes a rotation, for the reason
 * `models.ts` turns the conveyor geometry at load rather than nudging
 * `FACING_YAW`: those are two independent errors and they compose. `FACING_YAW`
 * shipped backwards for months precisely because it was the tempting place to
 * absorb somebody else's offset, and every test compared the renderer to the
 * same wrong constant.
 *
 * Negative because a clockwise quarter turn is negative in this scene — the
 * same sense that makes `FACING_YAW` east negative.
 */
const TURRET_MODEL_FORWARD = -Math.PI / 2;

/** Reused for a turret's bearing, which is the one per-instance rotation here. */
const TURN = new Quaternion();
const UP = new Vector3(0, 1, 0);
/**
 * A matrix that draws nothing.
 *
 * An `InstancedMesh` draws `count` entries from the front of its buffer, so a
 * turret standing in the *store* pool cannot simply be skipped — the slot would
 * be filled by whatever was there last frame, which is somebody else's crate on
 * a square that has a gun on it. Scaling it to nothing is the honest way to
 * leave a hole in a pool that has no holes.
 */
const EMPTY = new Matrix4().makeScale(0, 0, 0);

/**
 * How fast a gun swings, in radians a second, and where it rests.
 *
 * A turret turns to face what it is shooting and then, once the shooting is
 * over, settles to the nearest eighth-turn — so an idle battery reads as
 * *ordered* rather than as a row of guns frozen mid-sweep at whatever bearing
 * the last fight left them. The eighth rather than the quarter because the
 * board is approached at an eighth-turn yaw, so the diagonals are as much a
 * resting bearing here as the axes are.
 */
const TURRET_SWING = 6;
const TURRET_REST = Math.PI / 4;
/**
 * Scratch for the per-ingot maths.
 *
 * Module-level, because this runs once per ingot per frame and a board with a
 * working factory has hundreds. The old code built a fresh `Vector3` and
 * `Quaternion` inside the loop for every one of them.
 */
const CARGO_A = new Vector3();
const CARGO_B = new Vector3();
const CARGO_SCALE = new Vector3();
const BEAM_A = new Vector3();
const BEAM_B = new Vector3();
const BEAM_DIR = new Vector3();
const CARGO_TURN = new Quaternion();
const UP_AXIS = new Vector3(0, 1, 0);
/**
 * A held ingot against one riding a lane.
 *
 * Smaller, because a hopper's pile shares its square with the piece standing
 * on it — at full size a rook with six units on board wears a stack as tall as
 * it is.
 */
const HELD_SCALE = 0.62;
/** How much the pile swells while the oven is working. */
const HELD_COOK_SWELL = 0.28;

/**
 * The preview's treatment: translucent, never occluded, and green.
 *
 * Green because that is already the board's word for "this is what the click
 * you are about to make would do" — it is the legal-move colour, and the tiles
 * under a drawn run wear it too. Not the owner's belt colour, which would say
 * the lane is *there*: half the value of a preview is that it plainly is not.
 *
 * **It depth-tests like everything else on the board**, and that is a rule
 * that was bought rather than chosen. It used to punch through the army —
 * `depthTest: false`, the gameplay-overlay treatment the picker and the pawn
 * chevron take — because a run could be drawn *under* a piece, so the parts a
 * player most needed to see were exactly the ones with something standing in
 * front of them. Nothing is built under a piece now, so the case is gone; and
 * a ghost is not a pick target either, so there is nothing it can hide that
 * you could have clicked. An overlay that ignores depth in a scene with a
 * fixed 3/4 pitch reads as floating above the board rather than lying on it.
 */
const GHOST_OPACITY = 0.55;
/**
 * Zero: transparent meshes already draw after the opaque pass, and the deck
 * sits at its own height, so ordinary depth puts everything where it is. The
 * arrow takes one above the deck it lies on, which is coplanar-ish and the one
 * pair that cannot be settled by position alone.
 */
const GHOST_RENDER_ORDER = 0;
/** How long a unit takes to travel the square it just advanced. */
/**
 * How long a unit takes to travel its square.
 *
 * Longer than it was, and longer than a piece's move (0.26s), because it now
 * runs *after* the move rather than underneath it — this is the beat where the
 * factory does its turn, and a beat that is over before the eye finds it is
 * the same as no beat. It is also the only moving thing on screen at that
 * point, so it can afford the time.
 */
/**
 * How high a piece stands when it is on one of its own parts. Derived from the
 * squashed deck rather than typed twice, or the piece floats or sinks the
 * moment either number is tuned.
 *
 * `squash` is the deck's **live** height factor, not the settled one, so a
 * piece rides the bounce it caused. Left at the constant, the deck springs
 * back up through the piece's base on the way to settling — which is the one
 * frame of the whole effect a player would actually notice. Defaulted, so a
 * caller with no view to ask (a board drawn before the models land) still gets
 * the right resting height.
 */
export const pieceLift = (onBelt: boolean, squash = SHARED_SQUASH): number =>
  onBelt ? BELT_DECK * squash : 0;
/** How far a piece's held material sits from the square's centre. */
const HOLDING_OFFSET = 0.3;

/**
 * Never cull an instanced mesh whose instances are spread across the board.
 *
 * Three.js culls an `InstancedMesh` against a bounding sphere it computes
 * *once* and caches on the object; it does not recompute it when instance
 * matrices change. Every mesh here is built empty and filled per frame, so the
 * cached sphere is the empty one — centre at the origin, radius -1 — and it
 * stays that way for the life of the mesh.
 *
 * The symptom is that everything drawn by one of these vanishes *at once*,
 * intermittently, depending on where the camera happens to be: an empty sphere
 * at the origin passes the frustum test while the view is near the middle of a
 * small board and fails it as soon as you pan away or the board grows. Belts
 * disappearing was this.
 *
 * Culling is off rather than the sphere being recomputed every frame, because
 * culling an instanced mesh is all-or-nothing — it can only ever remove
 * *every* belt, never the off-screen ones — so it was never buying anything.
 */
function spansTheBoard(mesh: InstancedMesh): InstancedMesh {
  mesh.frustumCulled = false;
  return mesh;
}

/** Do two highlight maps say the same thing? A rim's colour is part of it. */
function sameOutlines(
  a: ReadonlyMap<number, number>,
  b: ReadonlyMap<number, number>
): boolean {
  if (a.size !== b.size) return false;
  for (const [square, color] of a) if (b.get(square) !== color) return false;
  return true;
}

export class IndustryViews {
  readonly group = new Object3D();

  private belts: InstancedMesh;
  /** Corner pieces, one mesh per direction of turn. */
  private curves = {} as Record<Hand, InstancedMesh>;
  private curveCounts: Record<Hand, number> = { right: 0, left: 0 };
  private beltArrows: InstancedMesh;
  /**
   * The same three meshes again, for a run that is only being drawn.
   *
   * A separate set rather than extra instances on the real ones: they take a
   * different material — translucent and never occluded — and an
   * `InstancedMesh` has exactly one.
   */
  private ghostBelts: InstancedMesh;
  private ghostCurves = {} as Record<Hand, InstancedMesh>;
  /**
   * Belt geometries this view *made*, as against the ones it was handed.
   *
   * The kit's own `conveyor.geometry` belongs to the model registry and is
   * shared with everything else that draws one, so disposing it on a pack swap
   * would empty the board. The bends are ours, the mirrored corner is ours, and
   * those are exactly the ones a swap has to free. `dispose` takes the list too,
   * so the two paths free the same things.
   */
  private ownedBelts: BufferGeometry[] = [];
  /** The kit's straight, kept so a pack swap can go back to it. */
  private kitBelt: BufferGeometry;
  private ghostArrows: InstancedMesh;
  private ghosts: readonly BeltGhost[] = [];
  private cargo: InstancedMesh;
  private holdings: InstancedMesh;
  /** An ore field: the tint on the ground, and the rock lying on it. */
  private oreWash: InstancedMesh;
  private oreLumps: InstancedMesh;
  /** A building: the pad it stands on, and the crate on top of it. */
  private storePads: InstancedMesh;
  private storeCrates: InstancedMesh;
  /** A turret's own silhouette. Empty when the prop has not loaded. */
  private turrets?: InstancedMesh;
  private beams: InstancedMesh;
  private beamMaterial: MeshBasicMaterial;
  /** The volley on screen. Idle on every board with no turrets. */
  readonly fire = new TurretFire();
  /** One instanced mesh per piece type, for the wreck a heap is drawn as. */
  private wrecks = new Map<PieceType, InstancedMesh>();
  /** Seconds since the view was made, for the one thing that spins. */
  private elapsed = 0;
  private cargoColor = new Color();
  /** One base-colour map per grade, once loaded; empty until then. */
  private ingotTextures: Texture[] = [];
  private cargoMaterial?: MeshStandardMaterial;
  private holdingMaterial?: MeshStandardMaterial;
  private disposables: { dispose(): void }[] = [];

  constructor(
    kenney: Record<KenneyName, KenneyModel>,
    props?: PropGeometries,
    /** The piece shapes, so a wreck can be the piece that died. */
    pieces?: PieceGeometries,
    /** The shell workshop, shared with the army. See `Outlines`. */
    private outlines = new Outlines()
  ) {
    // Kenney's conveyor geometry — the rollers and side rails are what make a
    // belt read as a belt — but our own material, tinted per instance so each
    // side's network is identifiable. The kit's shared atlas renders these
    // near-black, which disappears against the board.
    // Flat shading so the rollers and side rails catch light as separate
    // facets — smooth-shaded, a lane of them merges into one featureless wall.
    const beltMaterial = new MeshStandardMaterial({
      roughness: 0.75,
      metalness: 0,
      flatShading: true
    });
    this.fading(beltMaterial);
    this.kitBelt = kenney.conveyor.geometry;
    this.belts = spansTheBoard(new InstancedMesh(kenney.conveyor.geometry, beltMaterial, MAX_BELTS));
    this.belts.receiveShadow = true;
    this.belts.castShadow = true;
    this.belts.count = 0;
    this.group.add(this.belts);

    // Two more meshes, for the two ways a lane can turn. A separate geometry
    // rather than a scaled instance because a bend is a different shape, and a
    // separate *pair* because a left turn is a right turn mirrored — and a
    // mirror cannot be expressed as a rotation, only as a negative scale,
    // which would turn every face inside out.
    for (const hand of ["right", "left"] as const) {
      const geometry = bendConveyor(kenney.conveyor.geometry, hand);
      const mesh = spansTheBoard(new InstancedMesh(geometry, beltMaterial, MAX_BELTS));
      mesh.receiveShadow = true;
      mesh.castShadow = true;
      mesh.count = 0;
      this.group.add(mesh);
      this.curves[hand] = mesh;
      // In `ownedBelts` rather than `disposables`, because a pack swap frees
      // these long before teardown does. See `setBeltGeometries`.
      this.ownedBelts.push(geometry);
    }

    // The direction mark the conveyor mesh cannot give us. Dark rather than
    // accent-orange: it sits on an owner-coloured part and has to read against
    // both, and orange is already the pawn chevron's language.
    // Offset to the part's *outgoing* edge, not its centre: cargo rides the
    // middle of the deck and would sit straight on top of a centred mark. It
    // also says something true — the chevron is at the end the cargo leaves by.
    const arrowGeometry = createChevronGeometry({ radius: 0.3, scale: 1.15 });
    const arrowMaterial = new MeshStandardMaterial({
      color: PALETTE.beltArrow,
      roughness: 0.5,
      metalness: 0,
      flatShading: true
    });
    this.beltArrows = spansTheBoard(
      new InstancedMesh(arrowGeometry, arrowMaterial, MAX_BELTS)
    );
    this.beltArrows.count = 0;
    this.group.add(this.beltArrows);
    this.disposables.push(arrowGeometry);
    this.fading(arrowMaterial);

    // The preview, sharing every geometry above so a ghost is the exact shape
    // of the part it promises — including the bend, which is the one thing a
    // drawn run gets wrong often enough to be worth showing before it is laid.
    const ghostMaterial = new MeshStandardMaterial({
      color: PALETTE.legalMove,
      roughness: 0.75,
      metalness: 0,
      flatShading: true,
      transparent: true,
      opacity: GHOST_OPACITY,
      // Depth *tested*, so it is occluded by whatever is genuinely in front of
      // it; not depth-*written*, or the two translucent halves of a ghost
      // (deck and arrow) would clip each other.
      depthWrite: false
    });
    this.fading(ghostMaterial);
    this.ghostBelts = this.makeGhost(kenney.conveyor.geometry, ghostMaterial);
    for (const hand of ["right", "left"] as const) {
      this.ghostCurves[hand] = this.makeGhost(this.curves[hand].geometry, ghostMaterial);
    }
    // The chevron on top, one order higher so it is not lost inside the deck
    // it lies on — the one pair here that position alone does not separate.
    const ghostArrowMaterial = new MeshStandardMaterial({
      color: PALETTE.beltArrow,
      roughness: 0.5,
      metalness: 0,
      flatShading: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false
    });
    this.fading(ghostArrowMaterial);
    this.ghostArrows = this.makeGhost(arrowGeometry, ghostArrowMaterial, 1);

    // What a *piece* is holding, drawn with the same box as cargo on a belt:
    // "a box is material" stays one idea, and the only difference is that this
    // one sits at the piece's feet rather than on a part. Without it the
    // economy spends most of its life invisible — ore inside a pawn, a batch
    // in a bishop's oven, a foundry's stock — and the board stops explaining
    // itself (PLAN §7.1).
    // The same ingot as cargo: a unit of material is a unit of material
    // whether it is riding a lane or sitting inside a piece, and drawing it
    // two ways would be two vocabularies for one idea.
    const holdingGeometry = props?.ingot.clone() ?? new BoxGeometry(1, 0.7, 1);
    const holdingMaterial = new MeshStandardMaterial({
      roughness: 0.45,
      metalness: 0.65
    });
    this.holdings = spansTheBoard(new InstancedMesh(holdingGeometry, holdingMaterial, MAX_HOLDINGS));
    this.holdings.castShadow = true;
    this.holdings.count = 0;
    this.group.add(this.holdings);
    this.disposables.push(holdingGeometry);
    this.fading(holdingMaterial);

    // An ingot, not a box. Cargo, a piece's holdings and a foundry's stock are
    // all "one unit of material", so they share the model — and the grade is
    // the metal it is made of rather than a colour swatch on a crate.
    const cargoGeometry = props?.ingot.clone() ?? new BoxGeometry(1, 0.7, 1);
    const cargoMaterial = new MeshStandardMaterial({
      roughness: 0.45,
      metalness: 0.65
    });
    this.cargoMaterial = cargoMaterial;
    this.holdingMaterial = holdingMaterial;
    this.cargo = spansTheBoard(new InstancedMesh(cargoGeometry, cargoMaterial, MAX_CARGO));
    this.cargo.castShadow = true;
    this.cargo.count = 0;
    this.group.add(this.cargo);
    this.disposables.push(cargoGeometry);
    this.fading(cargoMaterial);


    // A wreck per piece type: scrap is a *corpse*, and an anonymous cone threw
    // away what a player most wants at a glance — what died here, and whose it
    // was. Small and pushed to one side of the square, because ore and
    // trinkets share the square with it and a pile in the middle buries them.
    //
    // Grey, and lit flat: a wreck should read as the piece it was and never as
    // a piece that is still playing. The side is carried in the tint rather
    // than in the shape, so a white and a black rook are the same silhouette
    // and different metal, which is what they are.
    if (pieces) {
      const wreckMaterial = new MeshStandardMaterial({
        roughness: 0.95,
        metalness: 0.15,
        flatShading: true
      });
      this.fading(wreckMaterial);
      for (const type of WRECK_TYPES) {
        const mesh = spansTheBoard(
          new InstancedMesh(pieces[type], wreckMaterial, MAX_WRECKS)
        );
        mesh.castShadow = true;
        mesh.count = 0;
        this.group.add(mesh);
        this.wrecks.set(type, mesh);
      }
    }

    // An ore field. Both pools take their colour per instance rather than per
    // pool, so one mesh serves all three lines — a mesh per line would be
    // three pools to grow and three to keep in step, for one number that
    // differs.
    const washGeometry = new PlaneGeometry(0.98, 0.98).rotateX(-Math.PI / 2);
    const washMaterial = new MeshBasicMaterial({
      transparent: true,
      opacity: ORE_WASH_OPACITY,
      // Terrain lies *under* everything: a lane, a piece and a marker all sit
      // on the same square, and a wash that wrote depth would z-fight the deck
      // it is painted on.
      depthWrite: false
    });
    this.oreWash = spansTheBoard(
      new InstancedMesh(washGeometry, washMaterial, MAX_ORE_TILES)
    );
    this.oreWash.count = 0;
    this.group.add(this.oreWash);
    this.disposables.push(washGeometry, washMaterial);

    const lumpGeometry = createOreLumpGeometry();
    const lumpMaterial = new MeshStandardMaterial({
      roughness: 0.9,
      metalness: 0.05,
      flatShading: true
    });
    this.oreLumps = spansTheBoard(
      new InstancedMesh(lumpGeometry, lumpMaterial, MAX_ORE_TILES * MAX_LUMPS)
    );
    this.oreLumps.castShadow = true;
    this.oreLumps.count = 0;
    this.group.add(this.oreLumps);
    this.disposables.push(lumpGeometry);
    this.fading(lumpMaterial);

    // A building, in two parts, because one would be wrong in both directions.
    //
    // The crate alone reads as "this square is a lot of material" — the exact
    // sentence "a box is material" already writes everywhere else, and the
    // reason PLAN §14.8 says a structure needs a silhouette of its own. The
    // pad alone reads as a tile. Together they read as *built*: a square that
    // is no longer ground, with something standing on it, tinted like the
    // lanes that feed it so you can see whose it is across the board.
    const padGeometry = new BoxGeometry(PAD_INSET, PAD_HEIGHT, PAD_INSET);
    const padMaterial = new MeshStandardMaterial({
      roughness: 0.85,
      metalness: 0.05,
      flatShading: true
    });
    this.storePads = spansTheBoard(
      new InstancedMesh(padGeometry, padMaterial, MAX_STORES)
    );
    this.storePads.receiveShadow = true;
    this.storePads.castShadow = true;
    this.storePads.count = 0;
    this.group.add(this.storePads);
    this.disposables.push(padGeometry);
    this.fading(padMaterial);

    const crateMaterial = new MeshStandardMaterial({
      color: PALETTE.store,
      roughness: 0.7,
      metalness: 0.1,
      flatShading: true
    });
    this.storeCrates = spansTheBoard(
      new InstancedMesh(kenney["box-large"].geometry, crateMaterial, MAX_STORES)
    );
    this.storeCrates.castShadow = true;
    this.storeCrates.receiveShadow = true;
    this.storeCrates.count = 0;
    this.group.add(this.storeCrates);
    this.fading(crateMaterial);

    // **A turret is not a crate.** Every structure used to draw as pad plus
    // crate, turret included — so the gun the run has to earn a keystone for
    // was indistinguishable from a warehouse, and the model that ships for it
    // was never loaded at all. Its own pool because it is its own silhouette,
    // and because it turns: a crate has no bearing to show and a gun's whole
    // state is which way it is pointing.
    //
    // Its authored material, not the palette — see `PropGeometries.turret`.
    if (props?.turret) {
      this.turrets = spansTheBoard(
        new InstancedMesh(props.turret.geometry, props.turret.material, MAX_STORES)
      );
      this.turrets.castShadow = true;
      this.turrets.receiveShadow = true;
      this.turrets.count = 0;
      this.group.add(this.turrets);
      this.disposables.push(props.turret.geometry);
    }

    // The laser. A unit cylinder along +Y, so one instance is stretched and
    // turned onto the line between a gun and what it hit — the same trick the
    // bent belt uses, which is that a shape you can scale is worth more than a
    // shape that is already the right size.
    //
    // **Unlit and additive**, the rule the king's thrusters already pay for: a
    // `MeshStandardMaterial` beam is lit by the key light, so it goes darker on
    // the shadowed side of the board and dimmest exactly where it crosses the
    // piece it is supposed to be burning.
    const beamGeometry = new CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS, 1, 6, 1, true);
    beamGeometry.translate(0, 0.5, 0);
    this.beamMaterial = new MeshBasicMaterial({
      color: PALETTE.beam,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false
    });
    this.beams = spansTheBoard(new InstancedMesh(beamGeometry, this.beamMaterial, MAX_BEAMS));
    this.beams.count = 0;
    // A beam is a mark rather than a thing on the board: it casts no shadow and
    // is never a pick target, exactly as an outline is not.
    this.beams.raycast = () => {};
    this.group.add(this.beams);
    this.disposables.push(beamGeometry);
    this.disposables.push(this.beamMaterial);

    // The shapes a building's rim is made of. Held rather than looked up per
    // paint: they are the pad's and the crate's own geometry, welded once.
    this.shellShapes = {
      pad: this.outlines.hullFor(padGeometry),
      crate: this.outlines.hullFor(kenney["box-large"].geometry),
      gun: props?.turret ? this.outlines.hullFor(props.turret.geometry) : undefined
    };
  }

  /**
   * A highlighted building wears the same inverted hull a piece does, in two
   * parts because it is drawn in two — the pad and the crate together are what
   * reads as *built*, so a rim round the crate alone would describe a box.
   *
   * Plain meshes rather than instances: a highlight is one square, sometimes
   * two, and an `InstancedMesh` has one material where this wants one per
   * colour. Pooled and grown all the same, never truncated.
   */
  private shellShapes!: { pad: BufferGeometry; crate: BufferGeometry; gun?: BufferGeometry };
  private shells: { pad: Mesh; crate: Mesh; gun?: Mesh }[] = [];
  private outlined: ReadonlyMap<number, number> = new Map();

  /**
   * Which buildings wear a rim, and in what colour. Squares holding a piece
   * rather than a structure are ignored — `PieceViews` is handed the same map.
   *
   * Redrawn at once, like the belt preview: a highlight follows the pointer
   * and nothing about the game has happened.
   */
  setOutlines(outlined: ReadonlyMap<number, number>): void {
    // Compared rather than taken on trust: this is asked on every state change
    // as well as on every pointer move, and a repaint sweeps the whole board.
    if (sameOutlines(this.outlined, outlined)) return;
    this.outlined = outlined;
    this.draw();
  }

  /** One building's rim, placed exactly where its pad and crate are. */
  private placeShell(
    index: number,
    square: number,
    color: number,
    matrix: Matrix4,
    position: Vector3,
    scale: Vector3,
    kind: StructureName = "store"
  ): void {
    while (this.shells.length <= index) {
      const make = (geometry: BufferGeometry): Mesh => {
        const mesh = new Mesh(geometry, this.outlines.materialFor(color));
        mesh.castShadow = false;
        mesh.receiveShadow = false;
        mesh.frustumCulled = false;
        // Not a thing on the board: the building underneath is what a click
        // lands on, and a store is already an exception to `pick`'s cell test.
        mesh.raycast = () => {};
        mesh.matrixAutoUpdate = false;
        this.group.add(mesh);
        return mesh;
      };
      this.shells.push({
        pad: make(this.shellShapes.pad),
        crate: make(this.shellShapes.crate),
        // A turret's rim is the turret's own shape. Drawn from the pad and the
        // crate it would be a rim round two things that are not there — the
        // pad is gone under a gun and the crate never was one.
        gun: this.shellShapes.gun ? make(this.shellShapes.gun) : undefined
      });
    }

    const shell = this.shells[index];
    const material = this.outlines.materialFor(color);
    // Writing `matrix` under `matrixAutoUpdate` off does **not** mark the
    // world matrix dirty — only `updateMatrix` does, and that is the call
    // being skipped. The group never moves, so a stale one is invisible until
    // the *ground* does: a store on a region still rising would wear its rim
    // at the height the floor finished at.
    const set = (mesh: Mesh, composed: Matrix4): void => {
      mesh.material = material;
      mesh.matrix.copy(composed);
      mesh.matrixWorldNeedsUpdate = true;
      mesh.visible = true;
    };

    if (kind === "turret") {
      // Only the gun, turned the way the gun is turned — a rim that did not
      // follow the bearing would swing away from the thing it is outlining
      // every time the turret took aim.
      shell.pad.visible = false;
      shell.crate.visible = false;
      if (shell.gun) {
        this.place(square, 0, position);
        TURN.setFromAxisAngle(UP, this.turnOf(square));
        set(shell.gun, matrix.compose(position, TURN, ONE));
      }
      return;
    }
    if (shell.gun) shell.gun.visible = false;

    this.place(square, PAD_HEIGHT / 2, position);
    set(shell.pad, matrix.compose(position, NO_TURN, ONE));

    this.place(square, PAD_HEIGHT, position);
    scale.set(CRATE_SCALE, CRATE_SCALE * CRATE_HEIGHT, CRATE_SCALE);
    set(shell.crate, matrix.compose(position, NO_TURN, scale));
  }

  /**
   * Animation state, keyed by square. The factory used to redraw itself from
   * scratch every time state changed, so belts blinked into existence, cargo
   * teleported a square, and heaps stepped down — a whole turn of industry
   * happened with nothing on screen appearing to move, which is the same
   * complaint the action pulse fixed for pieces.
   *
   * Everything here is a *display* value chasing the real one. None of it is
   * game state and none of it feeds back into the rules.
   */
  private anim = new Map<number, BeltAnim>();
  private lastHeap = new Map<number, number>();
  /**
   * Per-ingot motion: where each one was, so it can move to where it is.
   *
   * Fed by `TickFacts.cargo`, which the flow pass produces — the renderer
   * cannot work out which ingot is which from the board, because a unit is a
   * packed word with no identity beyond its slot in a pile.
   */
  private cargoMotion = new CargoMotion();
  /** This frame's resting spots, handed to `CargoMotion` once drawing is done. */
  private restingSpots = new Map<number, IngotSpot>();

  /**
   * The ovens this tick pulses, taken from the report rather than from the run.
   *
   * `CargoMotion` is pure and stays that way: it is asked *whether* a square is
   * cooking right now (`cooking`), which is what drawing needs, and reading its
   * pulse set back out would be a second job for a model with one. The same
   * list it was built from is right here.
   */
  private cookSquares: readonly number[] = [];
  /**
   * The pass that was on screen when this last looked.
   *
   * A transition is only visible across `advance`, and only from here: the
   * model has no notion of "since you last asked". Reset by `sync`, so each
   * tick's passes are announced once — a run beginning on "cook" is a real
   * case (a factory that only cooked) and would otherwise never be seen to
   * start, since `begin` puts it on screen without an `advance`.
   */
  private lastPhase: Phase | null = null;
  /**
   * Told when a pass begins, with the squares whose ovens are lit.
   *
   * The squares are meaningful for `"cook"` alone — nothing else pulses — and
   * are empty otherwise rather than absent, so a caller switching on the phase
   * has one shape to read. A pass skipped whole by one long frame is not
   * announced, exactly as it is not drawn.
   */
  private onPhase?: (phase: Phase, cooking: readonly number[]) => void;

  setOnPhase(onPhase: (phase: Phase, cooking: readonly number[]) => void): void {
    this.onPhase = onPhase;
  }

  /**
   * An instanced pool for the preview: the real geometry, so the ghost is the
   * exact shape of what it promises, with a translucent overlay material.
   */
  /**
   * Draw belts with a pack's own conveyor, or with the kit's again.
   *
   * `undefined` restores Kenney's, so a pack swap in either direction is one
   * call and neither is a special case — a board that has been through a pack
   * with belts and back must look exactly like one that never left.
   *
   * **Six meshes, and the ghosts share the real ones' geometry.** A ghost is
   * the shape of the part it promises, so a drawn run has to change with the
   * pack; the ghosts differ only in material.
   *
   * **A corner is optional even when a straight is not.** Without one the
   * straight is bent, exactly as the kit's is, so a pack can ship half of this
   * and still look like itself on a lane that turns.
   */
  setBeltGeometries(belts?: PackBelts): void {
    const straight = belts?.straight ?? this.kitBelt;
    // The authored corner is the *right* hand — a pack's cutter exports it
    // turning that way — and the left is its mirror, winding reversed.
    // Bending covers a pack that ships no corner at all.
    const corner = belts?.corner;
    const turns: Record<Hand, BufferGeometry> = corner
      ? { right: corner, left: mirrorConveyor(corner) }
      : { right: bendConveyor(straight, "right"), left: bendConveyor(straight, "left") };

    // Freed *after* the new set is built, never before: `bendConveyor` reads
    // the straight it is given, and on the way back to the kit that straight
    // is the one currently installed.
    //
    // An authored corner belongs to the pack that loaded it, so only the
    // mirror is ours to free; a bent pair is both.
    const stale = this.ownedBelts;
    this.ownedBelts = corner ? [turns.left] : [turns.right, turns.left];

    this.belts.geometry = straight;
    this.ghostBelts.geometry = straight;
    for (const hand of ["right", "left"] as const) {
      this.curves[hand].geometry = turns[hand];
      this.ghostCurves[hand].geometry = turns[hand];
    }

    for (const geometry of stale) geometry.dispose();
    // The instance matrices still describe the old shapes' placements, which
    // are the same placements — a part is a part wherever its geometry came
    // from — but nothing has asked for a frame since, and the on-demand loop
    // will not draw one on its own.
    this.draw();
  }

  private makeGhost(
    geometry: BufferGeometry,
    material: MeshStandardMaterial,
    orderAbove = 0
  ): InstancedMesh {
    const mesh = spansTheBoard(new InstancedMesh(geometry, material, MAX_BELTS));
    mesh.renderOrder = GHOST_RENDER_ORDER + orderAbove;
    mesh.count = 0;
    this.group.add(mesh);
    return mesh;
  }

  /**
   * The run currently being drawn, or an empty list to clear it.
   *
   * Redraws at once rather than waiting for the next state change: the whole
   * point is that it follows the pointer, and nothing about the game has
   * happened yet.
   */
  /** The session's drawn run, in the contract's names, keyed for `FACING_YAW`. */
  setGhosts(parts: readonly ApiBeltGhost[]): void {
    if (parts.length === 0 && this.ghosts.length === 0) return;
    this.ghosts = parts.map((ghost) => ({
      square: ghost.square,
      direction: DIR_INDEX[ghost.direction],
      ...(ghost.inbound ? { inbound: DIR_INDEX[ghost.inbound] } : {}),
      repoint: ghost.repoint
    }));
    this.draw();
  }

  /** Advance the factory's animations. True while anything is still moving. */
  /**
   * Give the material its metal grain.
   *
   * One map shared by every instance, tinted per grade by `setColorAt` — an
   * InstancedMesh has a single material, so a texture per grade would mean a
   * mesh per grade. The grain is what makes a unit read as *metal*; the tint
   * is what says which one, and it reuses the mechanism the grade colours
   * already had.
   */
  setIngotTextures(textures: Texture[]): void {
    this.ingotTextures = textures;
    // Silver, not iron: the map is multiplied by the per-instance tint, so the
    // grain has to be near-neutral or every grade comes out the colour of the
    // grain. Iron's brown turned gold to olive and diamond to sludge.
    const grain = textures[1] ?? textures[0];
    for (const material of [this.cargoMaterial, this.holdingMaterial]) {
      if (grain && material) {
        material.map = grain;
        material.needsUpdate = true;
      }
    }
  }

  /**
   * How far the *square itself* is from being seated, which is not zero while
   * new ground is rising into place.
   *
   * A wave opens ground and lands a camp's scrap and lanes on it in the same
   * command, so every belt, ingot, heap and pad here has to come up with its
   * tile — otherwise a run's most-watched moment is a floor arriving under a
   * factory that was already floating there.
   */
  private ground: (square: number) => number = () => 0;

  setGround(ground: (square: number) => number): void {
    this.ground = ground;
  }

  /**
   * Every material that draws on the board, and whether it is see-through
   * anyway.
   *
   * The second half is what stops `setRising` breaking the belt preview: a
   * ghost is *authored* transparent, so switching the flag off after a rise
   * would make the thing whose entire job is to look provisional look built.
   * Recorded at registration, when it is still whatever the constructor said.
   */
  private risable: { material: Material; always: boolean }[] = [];
  private rising = false;

  /**
   * Register a material: dispose it with the rest, and fade it below the board.
   *
   * Through the disposal funnel rather than beside it, because "every material
   * this class makes" is a list that already exists and a second one would be
   * a list to forget to add to — which here means one pool that stays solid
   * while every other thing on its square fades, on the one frame anybody is
   * watching.
   */
  private fading<T extends Material>(material: T): T {
    this.disposables.push(material);
    this.risable.push({ material, always: material.transparent });
    return fadeBelowBoard(material);
  }

  /**
   * Is the ground under the factory still arriving?
   *
   * A wave lands its scrap, its lanes and its cargo with its army, and all of
   * it rises welded to the tiles — so all of it has to be as invisible as they
   * are on the way up. `BoardRenderer` passes the tiles' answer down; the
   * factory has no rise of its own to consult.
   */
  setRising(rising: boolean): void {
    if (this.rising === rising) return;
    this.rising = rising;
    setTransparent(
      this.risable.filter((entry) => !entry.always).map((entry) => entry.material),
      rising
    );
  }

  /**
   * How far the piece that was standing on a square has travelled from it,
   * while it is mid-move. Null when nothing is in flight from there.
   *
   * A piece carries its material, so a move that left the ingots behind read
   * as the piece dropping them and picking them up again a square later. The
   * offset comes from `PieceViews` rather than being eased here, because the
   * arc is the piece's — a second easing beside it is two curves to keep in
   * step, and the frame they disagree on is the one the eye is following.
   */
  private travel: (square: number, out: Vector3) => Vector3 | null = () => null;

  setTravel(travel: (square: number, out: Vector3) => Vector3 | null): void {
    this.travel = travel;
  }

  /**
   * Squares that drew a pile of holdings on the last paint.
   *
   * Only so `followMotion` can tell a laden move from an empty one. Replaced
   * by each paint rather than accumulated, for the reason `restingSpots` is:
   * a square kept after its material left is a square this keeps repainting
   * for.
   */
  private heldSquares = new Set<number>();

  /**
   * Carry a flying piece's ingots along with it. True if any of them moved.
   *
   * Gated rather than repainting on every frame of every move: a paint sweeps
   * every live square, and most moves are made by a piece with nothing in its
   * hands. Asking `travel` about the few squares that drew a pile is the whole
   * test.
   */
  followMotion(): boolean {
    const held = this.heldSquares;
    for (const sq of held) {
      if (this.travel(sq, CARGO_B)) {
        this.draw();
        return true;
      }
    }
    return false;
  }

  /**
   * A building to carry: a build or a token dragged off the hotbar. Added to
   * this group so it is lit and shadowed as a building is; the caller places
   * it, and owes it `dispose`.
   */
  structureGhost(kind: StructureKind, owner: number): { object: Object3D; dispose(): void } {
    const ghost = buildStructureGhost(
      {
        pad: this.storePads.geometry,
        padHeight: PAD_HEIGHT,
        crate: this.storeCrates.geometry,
        crateScale: { x: CRATE_SCALE, y: CRATE_SCALE * CRATE_HEIGHT, z: CRATE_SCALE },
        turret: this.turrets
          ? { geometry: this.turrets.geometry, material: this.turrets.material as Material }
          : undefined
      },
      kind,
      owner
    );
    this.group.add(ghost.object);
    return {
      object: ghost.object,
      dispose: () => {
        this.group.remove(ghost.object);
        ghost.dispose();
      }
    };
  }

  /**
   * Every placement goes through here rather than calling `squareToWorld`
   * directly, so there is one place a square's height is decided and nothing
   * can be left behind on the floor when the floor moves.
   */
  private place(square: number, y: number, target?: Vector3): Vector3 {
    return squareToWorld(square, y + this.ground(square), target);
  }

  /**
   * Where each gun is pointing, and where it would rather point.
   *
   * Keyed by square and kept between frames, so a turret that has just fired
   * stays trained on what it shot instead of snapping back the instant the
   * laser is drawn. Display state and nothing else — the rules know what was
   * shot and never which way the barrel is turned, exactly as they know a lane
   * moved a unit and never where the ingot is on screen.
   */
  private turretAim = new Map<number, { yaw: number; want: number }>();

  /**
   * Every laser on screen, stretched onto the line between gun and target.
   *
   * A unit cylinder along +Y with its origin at the base, so the whole of the
   * placement is: point +Y down the line, scale Y to its length. `setFromUnitVectors`
   * is what turns "point at that" into a rotation without a matrix decompose.
   *
   * It rises out of the gun and lands part-way up whatever it hit, rather than
   * running square to square along the floor — a beam at ground level is drawn
   * *under* every piece on the board at this pitch, which is the one place it
   * must not be.
   */
  private drawBeams(matrix: Matrix4, position: Vector3, scale: Vector3): void {
    const beams = this.fire.beams();
    let count = 0;
    for (const beam of beams) {
      if (count >= MAX_BEAMS) break;
      this.place(beam.from, BEAM_FROM, position);
      BEAM_A.copy(position);
      this.place(beam.to, BEAM_TO, position);
      BEAM_B.copy(position);

      BEAM_DIR.subVectors(BEAM_B, BEAM_A);
      const length = BEAM_DIR.length();
      if (length < 1e-4) continue;
      TURN.setFromUnitVectors(UP, BEAM_DIR.normalize());
      // Thicker at the moment it fires and thinning as it fades, so a beam
      // reads as a discharge rather than as a rod that blinks out. Length is
      // never scaled — a laser that shortened would look like it missed.
      const fade = 1 - beam.through;
      scale.set(fade, length, fade);
      this.beams.setMatrixAt(count++, matrix.compose(BEAM_A, TURN, scale));
    }
    this.beams.count = count;
    this.beams.instanceMatrix.needsUpdate = true;
    // One material for the pool, so the fade is the volley's youngest beam —
    // per-beam opacity would want per-instance colour, and the taper above
    // already carries the age of each one.
    this.beamMaterial.opacity = beams.length > 0 ? 0.9 : 0;
  }

  /** The bearing a gun is drawn at this frame. */
  private turnOf(sq: number): number {
    return (this.turretAim.get(sq)?.yaw ?? 0) + TURRET_MODEL_FORWARD;
  }

  /**
   * Train a gun on a square. Called as a shot is drawn.
   *
   * The world's own axes: file runs +X and rank runs −Z (the scene convention),
   * and `atan2` over that pair is the yaw that points local −Z at the target —
   * the same sense `FACING_YAW` is derived in, which is why east comes out
   * negative here too.
   */
  aimTurret(from: number, to: number): void {
    const dx = fileOf(to as Square) - fileOf(from as Square);
    const dz = -(rankOf(to as Square) - rankOf(from as Square));
    const aim = this.turretAim.get(from) ?? { yaw: 0, want: 0 };
    aim.want = Math.atan2(dx, -dz);
    this.turretAim.set(from, aim);
  }

  /**
   * Stand every gun down to its nearest eighth-turn.
   *
   * Called when the firing phase ends rather than per shot, or a battery would
   * begin settling while half of it was still shooting.
   */
  restTurrets(): void {
    for (const aim of this.turretAim.values()) {
      aim.want = Math.round(aim.want / TURRET_REST) * TURRET_REST;
    }
  }

  /** Swing the guns, by the shortest way round. */
  private swingTurrets(dt: number): boolean {
    let busy = false;
    for (const aim of this.turretAim.values()) {
      // Shortest angle, or a gun crossing north takes the long way round for
      // no reason a player could explain.
      let delta = aim.want - aim.yaw;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      if (Math.abs(delta) < 0.001) {
        aim.yaw = aim.want;
        continue;
      }
      const step = Math.sign(delta) * Math.min(Math.abs(delta), TURRET_SWING * dt);
      aim.yaw += step;
      busy = true;
    }
    return busy;
  }

  update(dt: number): boolean {
    let busy = false;
    this.elapsed += dt;

    if (this.cargoMotion.advance(dt)) busy = true;
    // A pass changing is an event, and this is the only place it can be seen:
    // `advance` is what moves the run on, and the model reports a state rather
    // than a change.
    const phase = this.cargoMotion.current;
    if (phase !== this.lastPhase) {
      this.lastPhase = phase;
      if (phase) this.onPhase?.(phase, phase === "cook" ? this.cookSquares : NO_SQUARES);
    }
    // The volley. Each gun is trained on its target at the instant it fires —
    // `advance` reports exactly the ones that just opened up, so the aim is a
    // single event rather than an instruction repeated every frame.
    for (const shot of this.fire.advance(dt)) this.aimTurret(shot.from, shot.to);
    if (this.fire.running) busy = true;
    else if (this.fire.done) {
      // Stood down together, once the last beam has faded, or a battery would
      // begin settling while half of it was still shooting.
      this.restTurrets();
      this.fire.clear();
    }
    if (this.swingTurrets(dt)) busy = true;

    for (const state of this.anim.values()) {
      if (stepBeltAnim(state, dt)) busy = true;
    }

    if (busy) this.draw();
    return busy;
  }

  private lastFacts?: BoardFacts;

  sync(facts: BoardFacts, cargo?: readonly CargoLeg[]): void {
    this.lastFacts = facts;

    // A production run is one phase: every ingot that changed places sets off
    // together and arrives together. Opened here rather than per square,
    // because that is what the tick is — one pass over every lane at once.
    this.cargoMotion.begin(cargo ?? [], this.handsAt);
    // The ovens this run lights, and a fresh start for the pass watcher: a new
    // tick's passes are new events even where the last run had not drained.
    // Deduplicated, because an oven cooking three units reports three moves and
    // is one oven — the same set `CargoMotion` pulses.
    const cooking = new Set<number>();
    for (const move of cargo ?? []) if (move.phase === "cook") cooking.add(move.to);
    this.cookSquares = cooking.size > 0 ? [...cooking] : NO_SQUARES;
    this.lastPhase = null;

    for (const [sq, square] of facts.squares) {
      const state = this.anim.get(sq) ?? newBeltAnim();
      const belt = builtBelt(square);

      if (belt) {
        const yaw = FACING_YAW[DIR_INDEX[belt.dir]];
        // A part laid this turn grows into place rather than blinking on.
        if (!state.present) {
          state.present = true;
          state.grow = 0;
          state.yaw = yaw;
        }
        state.targetYaw = nearestAngle(state.yaw, yaw);
        state.leaving = false;
        state.owner = belt.owner;
      } else if (state.present) {
        // Lifted. It goes on being drawn while it shrinks out — the draw pass
        // asks the *facts* which squares have a part, so without `leaving` a
        // removed one is simply absent on the very next frame and the shrink
        // that was already written here had never once played.
        state.present = false;
        state.leaving = true;
      }

      // A piece arriving on a part presses it flat, and one stepping off lets
      // it come back — through a spring, so the deck has give. This is the one
      // eased value on the board that overshoots on purpose.
      //
      // **Only where there is a part.** An entry exists for every square, so
      // asking about the piece alone set a spring running under all thirty-two
      // men on the opening board: nothing to see, since nothing is drawn
      // there, but half a second of the on-demand render loop staying awake
      // after every single state change.
      setLoaded(state, belt !== undefined && square.piece !== undefined);

      const heap = square.heap?.size ?? 0;
      state.targetHeap = heap;
      // A heap that has just appeared grows out of the ground rather than
      // blinking on. `lastHeap` is a Map keyed by square: it was an
      // `Int16Array(128)` indexed by a packed square, so every read was
      // `undefined`, `undefined === 0` is false, and this had never fired.
      if ((this.lastHeap.get(sq) ?? 0) === 0 && heap > 0) state.heap = 0;
      this.lastHeap.set(sq, heap);

      this.anim.set(sq, state);
    }

    this.draw();
  }

  /** Re-place everything at the current ground heights. For the rise. */
  redraw(): void {
    this.draw();
  }

  /**
   * How compressed the deck on this square is *right now*, 1 if it is bare.
   *
   * What `pieceLift` multiplies, so the piece standing on a part rides the
   * spring rather than hanging at the settled height while the deck bounces
   * underneath it.
   */
  squashAt(square: number): number {
    const state = this.anim.get(square);
    if (!state) return SHARED_SQUASH;
    return state.squash * (state.grow > 0 ? state.grow : 1);
  }

  private draw(): void {
    if (this.lastFacts) this.paint(this.lastFacts);
  }

  private paint(facts: BoardFacts): void {
    // Replaced, never cleared: `followMotion` is iterating the previous one at
    // the moment it calls `draw`, and emptying it underneath that loop is the
    // one thing a `Set` will not forgive.
    this.heldSquares = new Set();
    const matrix = new Matrix4();
    const position = new Vector3();
    const quaternion = new Quaternion();
    // A second one: a corner's deck and its arrow point different ways, and
    // reusing the scratch quaternion for both would leave the arrow wearing
    // the deck's rotation.
    const bendQuaternion = new Quaternion();
    const scale = new Vector3();
    const up = new Vector3(0, 1, 0);

    let beltCount = 0;
    let straightCount = 0;
    this.curveCounts.right = 0;
    this.curveCounts.left = 0;
    let cargoCount = 0;
    let holdingCount = 0;
    let washCount = 0;
    let lumpCount = 0;
    const wreckCounts = new Map<PieceType, number>();
    let storeCount = 0;
    let shellCount = 0;
    let turretCount = 0;

    // Room for everything that is about to be drawn.
    //
    // These were fixed ceilings — 256 belts, 1024 ingots — and the draw loops
    // simply *stopped* at them: a factory past the cap lost lanes, and nothing
    // said so. That is fine on sixty-four squares and wrong on a board that
    // grows every stage, which is the board this game is now about.
    //
    // Doubling, and never shrinking, so a big factory reallocates a handful of
    // times over a run rather than once per turn. Same reasoning as the tiles.
    this.reserve(facts);

    // Every square the facts carry: ground, a building's floor, and anything
    // lying on a square with no ground under it. `position` here is the
    // scratch Vector3 the instancing writes into.
    for (const [sq, square] of facts.squares) {
      const belt = builtBelt(square);
      if (belt) {
        const state = this.anim.get(sq);
        this.place(sq, TILE_TOP, position);
        // Displayed yaw, not the stored one: a rotate turns rather than snaps.
        quaternion.setFromAxisAngle(up, state?.yaw ?? FACING_YAW[DIR_INDEX[belt.dir]]);
        // A switched-off part sinks slightly, so "not running" is visible
        // without another symbol. `grow` is a new part rising into place.
        const on = belt.on;

        // A piece standing on its own part shares the square: the part is
        // squashed flat and the piece stands on top of it.
        //
        // It used to shuffle sideways instead, which read as the belt getting
        // out of the way — wrong, because the lane is still running under
        // there, and a lane that jinks around every piece standing on it stops
        // looking continuous. Compressing keeps the full footprint and the run
        // unbroken; `pieceLift` is what raises the piece onto it.
        //
        // Sprung and spread rather than snapped flat: the deck gives under the
        // weight, overshoots, and settles, and it bulges sideways by a share
        // of what it lost. `deckScale` owns both, so the two cannot drift.
        const deckSize = deckScale(state ?? newBeltAnim(), on);
        scale.set(deckSize.x, deckSize.y, deckSize.z);

        // A part where the lane turns is drawn bent, and a bend is placed by
        // the direction material *arrives* from rather than the one it leaves
        // by — that is the end the arc starts at.
        const corner = cornerOf(belt);
        const deck = corner ? this.curves[corner.hand] : this.belts;
        // Three counters, not one: `beltCount` counts *arrows*, which every
        // part gets, while each deck mesh counts only its own. Sharing one
        // would leave the straight mesh claiming instances it never wrote,
        // and an unwritten instance draws whatever was in that slot last.
        const deckIndex = corner ? this.curveCounts[corner.hand]++ : straightCount++;
        if (corner) {
          bendQuaternion.setFromAxisAngle(up, FACING_YAW[corner.inbound]);
        }
        deck.setMatrixAt(
          deckIndex,
          matrix.compose(position, corner ? bendQuaternion : quaternion, scale)
        );
        deck.setColorAt(deckIndex, this.cargoColor.setHex(armyColor(belt.owner)));

        // The chevron rides the part's deck, so it sinks with a part that is
        // switched off and stays legible on one that is running.
        this.place(sq, BELT_DECK * (on ? 1 : 0.55), position);
        this.beltArrows.setMatrixAt(
          beltCount,
          matrix.compose(position, quaternion, ONE)
        );
        beltCount++;

        // Every ingot at its own grade, dearest at the top so the pile reads
        // from above. Two lanes merging on a square is a *rule* — loads add
        // together and the grades stay separate inside the pile — and drawing
        // the whole thing in whichever grade happened to be on top made the
        // moment they met invisible.
        const carried = wordsWhere(square, "belt");
        if (carried.length > 0) {
          cargoCount = this.placeCargo(cargoCount, sq, carried, 0.42, matrix);
        }
      } else if (this.anim.get(sq)?.leaving) {
        // A part that has been lifted, on its way out. The facts stopped
        // carrying this square's part the moment the command applied, so
        // everything the deck needs comes off the animation: its own last yaw
        // and the owner it had. Drawn straight whatever it was — a bend is
        // the lane's, and there is no lane any more.
        //
        // Deck only: no arrow, no cargo. The chevron says which way material
        // goes and the cargo has already been set down on the floor
        // (`remove-belt` drops it intact), so both would be claims about a
        // part that no longer does anything.
        const state = this.anim.get(sq) as BeltAnim;
        this.place(sq, TILE_TOP, position);
        quaternion.setFromAxisAngle(up, state.yaw);
        const going = deckScale(state, true);
        scale.set(going.x, going.y, going.z);
        const deckIndex = straightCount++;
        this.belts.setMatrixAt(deckIndex, matrix.compose(position, quaternion, scale));
        this.belts.setColorAt(
          deckIndex,
          this.cargoColor.setHex(armyColor(state.owner))
        );
      }

      // Everything this piece is carrying, including whatever is in its oven.
      if (square.piece) {
        const held = wordsWhere(square, "held", "cooking");
        if (held.length > 0) {
          holdingCount = this.placeHeld(holdingCount, sq, held, 0, matrix);
        }
      }

      // **The costume, beside the holdings.** Drawn from the wreck pool, which
      // is already a small model per rank — upright, on the far side of the
      // square from the wreck, and in the impersonated army's colour, because
      // *which army* is the whole of what a disguise claims.
      const worn = square.piece?.disguisedAs;
      const wornRank = worn ? PIECE_TYPE[worn.type] : undefined;
      const token = wornRank !== undefined ? this.wrecks.get(wornRank) : undefined;
      if (worn && wornRank !== undefined && token) {
        const drawn = wreckCounts.get(wornRank) ?? 0;
        this.place(sq, TOKEN_LIFT, position);
        // Opposite the wreck's own offset, so a square with a corpse on it and
        // a spy standing over it draws both rather than one through the other.
        position.x += TOKEN_OFFSET;
        // Upright and facing the camera's default, unlike a wreck: a costume
        // held up is a thing being *shown*, and a fallen one is a thing that
        // is finished. No scatter for the same reason — a token is deliberate.
        quaternion.identity();
        scale.setScalar(TOKEN_SCALE);
        token.setMatrixAt(drawn, matrix.compose(position, quaternion, scale));
        token.setColorAt(drawn, this.cargoColor.setHex(armyColor(worn.side)));
        wreckCounts.set(wornRank, drawn + 1);
      }

      // **An ore field.** Terrain, so it is drawn first and lowest: the wash
      // goes on the deck under everything, and the rocks sit low enough that a
      // piece standing on the tile still reads.
      const ore = square.deposit;
      // **A zone's tint** (§31 D2): the ore wash's pool, in the zone's colour,
      // on ground no deposit is washing already.
      const zoneTint = !ore && square.zone ? PALETTE.zones[square.zone] : undefined;
      if (zoneTint !== undefined) {
        this.place(sq, ORE_WASH_Y, position);
        quaternion.identity();
        scale.setScalar(1);
        this.oreWash.setMatrixAt(washCount, matrix.compose(position, quaternion, scale));
        this.oreWash.setColorAt(washCount, this.cargoColor.setHex(zoneTint));
        washCount++;
      }
      if (ore) {
        const colour = PALETTE.lines[LINE_INDEX[ore.dominant.line]] ?? PALETTE.lines[0];
        this.place(sq, ORE_WASH_Y, position);
        quaternion.identity();
        scale.setScalar(1);
        this.oreWash.setMatrixAt(washCount, matrix.compose(position, quaternion, scale));
        this.oreWash.setColorAt(washCount, this.cargoColor.setHex(colour));
        washCount++;

        // **A rock per few units** (`lumpsFor`). Depletion then draws itself:
        // a tile being worked loses a lump every few digs, and a field thins
        // from wherever the digging is. Placed by a hash of the square and the
        // slot — never a roll, or the rocks twitch on every sync and two
        // clients draw one board two ways, which is the rule a wreck's yaw
        // already follows.
        for (let i = 0; i < lumpsFor(ore.fraction); i++) {
          const spot = scatter(Math.imul(sq, 0x27d4eb2d) ^ (i + 1));
          const spin = scatter(Math.imul(sq, 0x165667b1) ^ (i + 1));
          this.place(sq, ORE_LUMP_Y, position);
          position.x += (spot - 0.5) * ORE_LUMP_SPREAD * 2;
          position.z += (spin - 0.5) * ORE_LUMP_SPREAD * 2;
          quaternion.setFromAxisAngle(up, spin * Math.PI * 2);
          // A little variation in size, off the same hash, so a scatter of six
          // does not read as a row of identical beads.
          scale.setScalar(ORE_LUMP_SCALE * (0.75 + spot * 0.5));
          this.oreLumps.setMatrixAt(lumpCount, matrix.compose(position, quaternion, scale));
          this.oreLumps.setColorAt(lumpCount, this.cargoColor.setHex(colour));
          lumpCount++;
        }
      }

      const heapSize = square.heap?.size ?? 0;
      const heapShown = this.anim.get(sq)?.heap ?? heapSize;
      // Every heap is a wreck. Scrap seeded by a scenario has no corpse behind
      // it and the engine names a pawn's: material on the board is dead pieces
      // and ingots, and nothing else, so there is no third shape to fall back
      // to. A heap still shrinking out has no fact left and draws the pawn's.
      const rank = PIECE_TYPE[square.heap?.wreck ?? "pawn"];
      const wreck = this.wrecks.get(rank);

      if (wreck && (heapSize > 0 || heapShown > 0.05)) {
        const drawn = wreckCounts.get(rank) ?? 0;
        {
          // Lying down, and half sunk into the deck.
          //
          // A wreck resting neatly *on* the board still reads as a piece that
          // has been put down and could be picked up again. Half in the ground
          // reads as wreckage — something the board has started to take back —
          // and it also stops a pile of them looking like a tidy row of models
          // on a shelf. Centre at deck level is exactly half.
          this.place(sq, 0, position);
          // Pushed off centre so ore and trinkets have the other half of the
          // square. Along the file, so a lane running through the square is
          // not walked over by it.
          position.x -= WRECK_OFFSET;
          // Fallen, and not all fallen the same way. A row of wrecks lying at
          // an identical angle reads as a repeated asset; a pile reads as a
          // pile because nothing in it agrees with anything else.
          //
          // Derived from the square, never from `Math.random`. Two reasons,
          // and the second is the load-bearing one: a fresh roll on every sync
          // would make every wreck on the board twitch each time anything at
          // all changed, and two clients drawing one board have to draw it the
          // same — the renderer is not bound by `src/engine/rules/`'s determinism
          // rule, but "same state, same picture" is not a rule it gets to opt
          // out of either.
          quaternion.setFromAxisAngle(SPIN_AXIS, scatter(sq) * Math.PI * 2);
          quaternion.multiply(FALLEN.setFromAxisAngle(FALLEN_AXIS, Math.PI / 2));
          // Grows a little with the pile, so nine scrap still reads as more
          // than one — the shape says *what*, the size says *how much*.
          const bulk = WRECK_SCALE * (0.75 + Math.min(1, Math.sqrt(Math.max(0, heapShown)) / 3) * 0.5);
          scale.setScalar(bulk);
          wreck.setMatrixAt(drawn, matrix.compose(position, quaternion, scale));
          // Whose it was, as metal rather than as shape: a white and a black
          // rook are the same silhouette and different colour, which is what
          // they are.
          wreck.setColorAt(
            drawn,
            this.cargoColor.setHex(square.heap?.side === 0 ? PALETTE.wreckLight : PALETTE.wreckDark)
          );
          wreckCounts.set(rank, drawn + 1);
        }
      }

      // What is lying on the ground here: the metal that comes off a lane
      // when its part is lifted, or an altar's overflow.
      const heapTop = 0.12 + Math.sqrt(heapSize) * 0.13;
      const metal: number[] = [];
      for (const unit of square.units) {
        if (unit.where === "loose") metal.push(unitWord(unit));
      }
      // And the metal, as the pile of ingots it is — cheapest first, like
      // every other pile, so the valuable bars sit on top and the order is
      // stable. On the ground rather than at a foot: nothing is holding it.
      holdingCount = this.placeLoose(holdingCount, sq, metal, heapTop, matrix);

      // A building. Its square is a floor rather than ground — the facts carry
      // it as `structure-floor` — and it is drawn here in the one sweep, so it
      // takes the same ground height everything else on its square does.
      const structure = square.structure;
      if (structure) {
        // Its rim, if it is highlighted. In the sweep rather than a pass of its
        // own so it takes the same ground height the pad does — a building on
        // ground still rising would otherwise wear its outline at the settled
        // height.
        const outline = this.outlined.get(sq);
        if (outline !== undefined) {
          this.placeShell(shellCount++, sq, outline, matrix, position, scale, structure.kind);
        }

        // **A turret stands on the board, not on a pad.** The pad is what says
        // "this square is built on" for a warehouse, whose own silhouette is a
        // crate and would otherwise read as a pile of material — but a turret
        // is unmistakably a built thing, so the pad added nothing except a
        // bright square under it. The floor tile `BoardRenderer` lays under
        // every structure is what it stands on instead.
        const bare = structure.kind === "turret";
        this.place(sq, PAD_HEIGHT / 2, position);
        this.storePads.setMatrixAt(storeCount, bare ? EMPTY : matrix.compose(position, NO_TURN, ONE));
        this.storePads.setColorAt(storeCount, this.cargoColor.setHex(armyColor(structure.owner)));

        this.place(sq, PAD_HEIGHT, position);
        if (bare && this.turrets) {
          // Turned to whatever it is looking at. `turnOf` holds the bearing
          // between frames, so a gun that has just fired stays pointed at what
          // it shot rather than snapping back the instant the shot is drawn.
          TURN.setFromAxisAngle(UP, this.turnOf(sq));
          this.turrets.setMatrixAt(turretCount++, matrix.compose(position, TURN, ONE));
          // Its pad still counts as a building, so the crate pool has to be
          // told to skip this one — an instanced pool draws `count` entries
          // from the front, so leaving a hole would draw somebody else's crate
          // here.
          this.storeCrates.setMatrixAt(storeCount, EMPTY);
        } else {
          const [wide, tall] = BUILDING_SHAPE[structure.building] ?? [1, 1];
          scale.set(CRATE_SCALE * wide, CRATE_SCALE * CRATE_HEIGHT * tall, CRATE_SCALE * wide);
          this.storeCrates.setMatrixAt(storeCount, matrix.compose(position, NO_TURN, scale));
        }
        storeCount++;

        // What it is holding, drawn with the same box a piece uses. A store
        // that filled up in silence would be the economy going invisible again
        // at the one place it now collects — and "a box is material" stays one
        // idea. The engine hands `stock` cheapest first, like `units`.
        if (structure.stock.length > 0) {
          holdingCount = this.placeHeld(
            holdingCount,
            sq,
            structure.stock.map(unitWord),
            PAD_HEIGHT,
            matrix
          );
        }
      }
    }

    this.belts.count = straightCount;
    this.belts.instanceMatrix.needsUpdate = true;
    if (this.belts.instanceColor) this.belts.instanceColor.needsUpdate = true;
    for (const hand of ["right", "left"] as const) {
      const mesh = this.curves[hand];
      mesh.count = this.curveCounts[hand];
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    this.beltArrows.count = beltCount;
    this.beltArrows.instanceMatrix.needsUpdate = true;
    for (const [type, mesh] of this.wrecks) {
      mesh.count = wreckCounts.get(type) ?? 0;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    this.storePads.count = storeCount;
    this.storePads.instanceMatrix.needsUpdate = true;
    if (this.storePads.instanceColor) this.storePads.instanceColor.needsUpdate = true;
    this.storeCrates.count = storeCount;
    this.storeCrates.instanceMatrix.needsUpdate = true;
    if (this.turrets) {
      this.turrets.count = turretCount;
      this.turrets.instanceMatrix.needsUpdate = true;
    }
    this.drawBeams(matrix, position, scale);
    // The pool is grown and never shrunk, like every other one here, so what
    // is left over is hidden rather than removed.
    for (let i = shellCount; i < this.shells.length; i++) {
      this.shells[i].pad.visible = false;
      this.shells[i].crate.visible = false;
      if (this.shells[i].gun) this.shells[i].gun!.visible = false;
    }

    // Ingots on their way *into* a machine, which no pile draws because the
    // rules have already consumed them. Last, so they cannot be crowded out of
    // the pool by a pile that is still on the board.
    cargoCount = this.drawDepartures(cargoCount, matrix);

    // This frame's resting places become next tick's starting points. Handed
    // over whole: a map that accumulated would leave a stale spot under a key
    // no ingot occupies, and the next unit to land there would slide in from
    // wherever its predecessor stood several turns ago.
    this.cargoMotion.settle(this.restingSpots);
    this.restingSpots = new Map();

    this.paintGhosts(facts, matrix, position, quaternion, scale, up);

    this.holdings.count = holdingCount;
    this.holdings.instanceMatrix.needsUpdate = true;
    if (this.holdings.instanceColor) this.holdings.instanceColor.needsUpdate = true;
    this.cargo.count = cargoCount;
    this.cargo.instanceMatrix.needsUpdate = true;
    if (this.cargo.instanceColor) this.cargo.instanceColor.needsUpdate = true;
    this.oreWash.count = washCount;
    this.oreWash.instanceMatrix.needsUpdate = true;
    if (this.oreWash.instanceColor) this.oreWash.instanceColor.needsUpdate = true;
    this.oreLumps.count = lumpCount;
    this.oreLumps.instanceMatrix.needsUpdate = true;
    if (this.oreLumps.instanceColor) this.oreLumps.instanceColor.needsUpdate = true;
  }

  /**
   * Runs that are not track yet: the one being drawn, and the ones already
   * drawn and waiting for their iron.
   *
   * **The same picture for both, on purpose.** A blueprint and a gesture are
   * the same claim — *this is where the lane goes* — and the difference
   * between them is that one follows the pointer. Giving a blueprint a third
   * appearance would make the player learn two symbols for one idea, and the
   * gesture already reads as a gesture by moving.
   *
   * Deliberately the same arithmetic as the real pass above — same deck
   * height, same yaw, same bend placed by the direction material *arrives*
   * from, same squash under a piece — because the whole promise of a preview
   * is that laying it changes nothing but the colour. Anything computed a
   * second way here is a way for the two to disagree.
   */
  private paintGhosts(
    facts: BoardFacts,
    matrix: Matrix4,
    position: Vector3,
    quaternion: Quaternion,
    scale: Vector3,
    up: Vector3
  ): void {
    const bend = new Quaternion();
    let straight = 0;
    let arrows = 0;
    const curves: Record<Hand, number> = { right: 0, left: 0 };

    for (const ghost of blueprintGhosts(facts).concat(this.ghosts)) {
      // The arrow always, the deck only where there is not one already.
      //
      // Drawing a green deck over a part you already own would hide the lane
      // the gesture is *about* — a run drawn across your own track re-points
      // it, and what a player needs to see there is their own belt with a new
      // direction on it, not a preview covering it up.
      this.place(ghost.square, BELT_DECK, position);
      quaternion.setFromAxisAngle(up, FACING_YAW[ghost.direction]);
      this.ghostArrows.setMatrixAt(arrows++, matrix.compose(position, quaternion, ONE));
      if (ghost.repoint) continue;

      this.place(ghost.square, TILE_TOP, position);
      scale.set(1, 1, 1);
      // A part under a piece is squashed flat with the piece standing on it,
      // and a run may be drawn under one now — so the preview has to show the
      // same thing, or laying it would visibly change the lane's shape.
      if (facts.squares.get(ghost.square as Square)?.piece) scale.y *= SHARED_SQUASH;

      const corner =
        ghost.inbound === undefined ? null : bendBetween(ghost.inbound, ghost.direction);
      const deck = corner ? this.ghostCurves[corner.hand] : this.ghostBelts;
      const index = corner ? curves[corner.hand]++ : straight++;
      if (corner) bend.setFromAxisAngle(up, FACING_YAW[corner.inbound]);
      deck.setMatrixAt(index, matrix.compose(position, corner ? bend : quaternion, scale));
    }

    this.ghostBelts.count = straight;
    this.ghostBelts.instanceMatrix.needsUpdate = true;
    for (const hand of ["right", "left"] as const) {
      const mesh = this.ghostCurves[hand];
      mesh.count = curves[hand];
      mesh.instanceMatrix.needsUpdate = true;
    }
    this.ghostArrows.count = arrows;
    this.ghostArrows.instanceMatrix.needsUpdate = true;
  }

  /**
   * Grow every instanced mesh to fit what this board holds.
   *
   * Counted from the facts rather than from the draw loop, because a loop that
   * discovers it has run out has already skipped something. Cheap: one pass
   * over the squares, and the draw pass walks the same squares immediately
   * afterwards.
   */
  private reserve(facts: BoardFacts): void {
    let built = 0;
    let blueprints = 0;
    let cargo = 0;
    let held = 0;
    let loose = 0;
    let fields = 0;
    let lumps = 0;
    let structures = 0;
    // **Costumes share the wreck pool and must be counted into it**, or a
    // board with a spy on it silently stops drawing the last wreck — the
    // failure every pool here is grown to avoid, arriving through a second
    // customer rather than through a bigger board.
    const wrecks = new Map<PieceType, number>();
    const wreck = (type: PieceType): void => {
      wrecks.set(type, (wrecks.get(type) ?? 0) + 1);
    };

    for (const square of facts.squares.values()) {
      if (square.belt) {
        if (square.belt.blueprint) blueprints++;
        else built++;
      }
      for (const unit of square.units) {
        switch (unit.where) {
          case "belt":
            cargo++;
            break;
          case "held":
          case "cooking":
            held++;
            break;
          case "loose":
            loose++;
            break;
        }
      }
      if (square.structure) {
        structures++;
        // One box per store that is holding anything, on top of the pieces.
        if (square.structure.stock.length > 0) held++;
      }
      if (square.deposit || square.zone) {
        fields++;
      }
      if (square.deposit) {
        lumps += lumpsFor(square.deposit.fraction);
      }
      // The engine names every heap's wreck — a pawn's where no corpse is
      // recorded — so counting by the name fits every heap on the board.
      if (square.heap) wreck(PIECE_TYPE[square.heap.wreck]);
      if (square.piece?.disguisedAs) wreck(PIECE_TYPE[square.piece.disguisedAs.type]);
    }

    // Plus the parts being lifted, which the facts no longer carry and the
    // draw pass is still shrinking out. Counted, not guessed: a pool that runs
    // out does not warn, it stops drawing.
    let going = 0;
    for (const state of this.anim.values()) if (state.leaving) going++;
    const belts = built + going;
    // Every straight, every curve and every arrow could in principle be the
    // whole network, so they are each sized for it rather than for a share.
    this.belts = this.fit(this.belts, belts);
    this.curves.right = this.fit(this.curves.right, belts);
    this.curves.left = this.fit(this.curves.left, belts);
    this.beltArrows = this.fit(this.beltArrows, belts);

    // The preview, sized the same way and for the same reason: a run drawn
    // across a grown board can be longer than the pool it started with, and a
    // loop that runs out has already dropped the far end of what it was
    // promising to draw. Blueprints share the pool, being the same picture,
    // so they have to share the count.
    const drawn = this.ghosts.length + blueprints;
    this.ghostBelts = this.fit(this.ghostBelts, drawn);
    this.ghostCurves.right = this.fit(this.ghostCurves.right, drawn);
    this.ghostCurves.left = this.fit(this.ghostCurves.left, drawn);
    this.ghostArrows = this.fit(this.ghostArrows, drawn);

    // Plus the ones already consumed and still being drawn on their way in.
    // They are not on a belt any more — that is what being consumed means —
    // so counting only the piles would let the last thing a lane ever does be
    // the one thing dropped for want of a slot.
    this.cargo = this.fit(this.cargo, cargo + this.cargoMotion.travelling().length);

    // **Per ingot, not per piece.** A hopper is drawn as a pile of individual
    // bars now, so one loaded rook can cost `MAX_PILE` of these — and a pool
    // sized per piece silently stops drawing partway up the stack.
    //
    // Loose metal shares the holdings pool, because it is drawn the same way —
    // a pile of ingots that no motion is keyed to. It deliberately does *not*
    // go through `placeCargo`: that stakes a claim in `restingSpots` under
    // `spotKey(square, i)`, and a lane laid over a square that already has
    // metal lying on it would then share an identity with it and animate the
    // two as one ingot.
    this.holdings = this.fit(this.holdings, held * MAX_PILE + loose);

    // An ore field: one wash per tile, and `lumpsFor` rocks on it. Counted
    // rather than guessed, like every pool here — a field being dug shrinks,
    // so the lump count moves every tick.
    this.oreWash = this.fit(this.oreWash, fields);
    this.oreLumps = this.fit(this.oreLumps, lumps);

    // Buildings, both halves of each.
    this.storePads = this.fit(this.storePads, structures);
    this.storeCrates = this.fit(this.storeCrates, structures);

    for (const [type, mesh] of this.wrecks) {
      this.wrecks.set(type, this.fit(mesh, wrecks.get(type) ?? 0));
    }
  }

  /**
   * The same mesh if it already fits, or a bigger one in its place.
   *
   * `InstancedMesh` fixes capacity at construction, so growing means a new one
   * — but *capacity is not count*, and conflating them reallocates on every
   * change including the ones that shrink. The geometry and material are
   * shared and outlive every mesh that has pointed at them, so `dispose` here
   * releases the instance buffers and nothing else.
   */
  private fit(mesh: InstancedMesh, need: number): InstancedMesh {
    if (need <= mesh.instanceMatrix.count) return mesh;

    const capacity = Math.max(need, mesh.instanceMatrix.count * 2, 16);
    const grown = spansTheBoard(new InstancedMesh(mesh.geometry, mesh.material, capacity));
    grown.castShadow = mesh.castShadow;
    grown.receiveShadow = mesh.receiveShadow;
    // Or an overlay pool silently stops being an overlay the first time it
    // outgrows itself — which is the one moment nobody is looking for it.
    grown.renderOrder = mesh.renderOrder;
    grown.count = 0;
    this.group.remove(mesh);
    mesh.dispose();
    this.group.add(grown);
    return grown;
  }

  /**
   * A pile of ingots on a square.
   *
   * `grades` is one entry per ingot, in the order they are stacked — *not* one
   * grade for the pile. A square holding iron and gold used to be drawn
   * entirely in whichever was on top, so a lane merging two streams looked
   * like one stream and the moment two grades met was invisible. Merging is a
   * rule (loads that meet on a square add together, grades staying separate
   * inside the pile), and it should be a thing you can see.
   *
   * Size still comes from the grade, so a mixed pile is visibly mixed by
   * shape as well as colour.
   */
  private placeCargo(
    index: number,
    square: number,
    held: readonly number[],
    height: number,
    matrix: Matrix4
  ): number {
    const units = held.length;
    if (units === 0) return index;

    // A pile of ingots, one instance each, rather than one ingot inflated by
    // the cube root of the count. Ten units used to be one enormous bar; now
    // it is a stack you can count at a glance, which is the whole reason a
    // unit is drawn as an object rather than a number.
    const sizes = held.map(unitScale);
    let at = index;
    pileSlots(sizes).forEach((slot, i) => {
      // Against the pool's real capacity, which `reserve` has already grown
      // to fit. A constant here is a ceiling that silently drops ingots.
      if (at >= this.cargo.instanceMatrix.count) return;
      const unit = held[i] ?? held[held.length - 1] ?? 0;
      const size = slot.size;

      // Where this ingot belongs, as an offset from its square. Remembered in
      // that form rather than as a world point, because the square's own
      // height moves — ground still rising, a deck squashed under a piece.
      const resting: IngotSpot = {
        square,
        x: slot.x,
        y: height + slot.y,
        z: slot.z
      };
      this.restingSpots.set(spotKey(square, i), resting);

      // Mid-move: draw it between where it was and where it is going. The two
      // may be different squares, so the blend has to happen in world space,
      // which is also the only place the ground offset is known.
      const arrival = this.cargoMotion.arrivalFor(square, i);
      const here = this.spotToWorld(resting, CARGO_A);
      let scale = size;
      if (this.cargoMotion.running) {
        // The board holds the *end* state of the whole tick, so an ingot whose
        // pass has not played yet is already sitting at its destination. It has
        // to wait its turn or the phases say nothing.
        const stage = this.cargoMotion.stageOf(arrival?.phase ?? "flow");
        if (stage < 0) scale = 0;
        else if (stage === 0) {
          if (arrival) here.lerp(this.spotToWorld(arrival.from, CARGO_B), 1 - this.cargoMotion.t);
          // Nothing was drawn here last tick, so it did not travel — a miner
          // put it on the lane. It grows in where it landed.
          else scale = size * this.cargoMotion.t;
        }
      }

      CARGO_TURN.setFromAxisAngle(UP_AXIS, slot.yaw);
      matrix.compose(here, CARGO_TURN, CARGO_SCALE.set(scale, scale, scale));
      this.cargo.setMatrixAt(at, matrix);
      this.cargo.setColorAt(at, this.cargoColor.setHex(unitColour(unit)));
      at++;
    });
    return at;
  }

  /**
   * What a piece or a building is holding, as a pile of individual ingots.
   *
   * **Not one box scaled by the cube root of the count.** That is what it was,
   * and it said "some material" where the belt beside it said "four bars of
   * iron and one of steel" — the same stuff, drawn two different ways, one of
   * them countable. Grade is size and colour per ingot, so a mixed hopper is
   * visibly mixed, which is the whole argument for drawing a unit as an object.
   *
   * Smaller than a lane's pile and pushed to the near-side foot: it shares the
   * square with a piece that is standing on it, and at this pitch a full-size
   * stack at the centre would be behind the model.
   */
  private placeHeld(
    index: number,
    square: number,
    held: readonly number[],
    base: number,
    matrix: Matrix4
  ): number {
    if (held.length === 0) return index;
    this.heldSquares.add(square);
    // A pulse while the oven is working. The cook pass moves nothing — the
    // banks and the oven are the same box — so a swell is the only thing that
    // can say it happened.
    const beat = this.cargoMotion.cooking(square)
      ? 1 + Math.sin(Math.PI * this.cargoMotion.t) * HELD_COOK_SWELL
      : 1;
    // Once for the pile, not once per ingot: they all ride the same arc, and
    // a store's stock takes the same path because a building never travels —
    // its square is not a cell, so no piece can have left it.
    const carried = this.travel(square, CARGO_B);

    let at = index;
    const sizes = held.map((unit) => unitScale(unit) * HELD_SCALE * beat);
    pileSlots(sizes).forEach((slot, i) => {
      if (at >= this.holdings.instanceMatrix.count) return;
      const unit = held[i] ?? held[held.length - 1] ?? 0;
      const size = slot.size;
      this.place(square, base + slot.y, CARGO_A);
      CARGO_A.x += -HOLDING_OFFSET + slot.x;
      CARGO_A.z += HOLDING_OFFSET + slot.z;
      if (carried) CARGO_A.add(carried);
      CARGO_TURN.setFromAxisAngle(UP_AXIS, slot.yaw);
      matrix.compose(CARGO_A, CARGO_TURN, CARGO_SCALE.set(size, size, size));
      this.holdings.setMatrixAt(at, matrix);
      this.holdings.setColorAt(at, this.cargoColor.setHex(unitColour(unit)));
      at++;
    });
    return at;
  }

  /**
   * Metal lying loose on a square, drawn as the pile of ingots it is.
   *
   * `dropped` holds *anything* set down — a relic a beaten king left, the
   * overflow from a sacrifice a king had no room for, and the cargo that comes
   * off a lane when its part is lifted. Only the first of those is a relic,
   * and drawing the rest as turning octahedra told the player their iron had
   * become treasure.
   *
   * Deliberately **not** `placeCargo`, which is otherwise the same picture:
   * that stakes a claim in `restingSpots` under `spotKey(square, i)`, which is
   * a lane's cargo identity. A part laid over a square that already has metal
   * lying on it would then share slots with it, and the next tick would
   * animate one ingot as though it were the other.
   *
   * At the square's centre rather than at a near-side foot: nothing is holding
   * it, and the foot offset is `placeHeld` saying "this belongs to the piece
   * standing here".
   */
  private placeLoose(
    index: number,
    square: number,
    held: readonly number[],
    base: number,
    matrix: Matrix4
  ): number {
    if (held.length === 0) return index;
    // Cheapest first, like every other pile — the engine's order, so the
    // valuable metal sits on top and nothing reshuffles when something is
    // added.
    const sizes = held.map((unit) => unitScale(unit) * HELD_SCALE);

    let at = index;
    pileSlots(sizes).forEach((slot, i) => {
      if (at >= this.holdings.instanceMatrix.count) return;
      const size = slot.size;
      this.place(square, base + slot.y, CARGO_A);
      CARGO_A.x += slot.x;
      CARGO_A.z += slot.z;
      CARGO_TURN.setFromAxisAngle(UP_AXIS, slot.yaw);
      matrix.compose(CARGO_A, CARGO_TURN, CARGO_SCALE.set(size, size, size));
      this.holdings.setMatrixAt(at, matrix);
      this.holdings.setColorAt(at, this.cargoColor.setHex(unitColour(held[i] ?? 0)));
      at++;
    });
    return at;
  }

  /** An ingot's spot as a world point, with the square's own height folded in. */
  private spotToWorld(spot: IngotSpot, out: Vector3): Vector3 {
    this.place(spot.square, spot.y, out);
    out.x += spot.x;
    out.z += spot.z;
    return out;
  }

  /**
   * Ingots on their way into a machine, which no pile will draw.
   *
   * They have already left `beltCargo` — the rules consumed them — so without
   * this the payoff moment of a whole lane is the one frame nothing is drawn
   * for. They shrink as they go, which is what says *consumed* rather than
   * *delivered and stacked*.
   */
  private drawDepartures(index: number, matrix: Matrix4): number {
    let at = index;
    const t = this.cargoMotion.t;
    for (const leaving of this.cargoMotion.travelling()) {
      if (at >= this.cargo.instanceMatrix.count) break;
      const from = this.spotToWorld(leaving.from, CARGO_A);
      const to = this.spotToWorld(leaving.to, CARGO_B);
      // Shrinking as it goes: it is being taken *in*, not set down, and an
      // ingot that lands at full size then vanishes reads as a dropped frame.
      const size = GRADE_SCALE[0] * (leaving.into ? 1 - t * 0.75 : 1);
      matrix.compose(from.lerp(to, t), NO_TURN, CARGO_SCALE.set(size, size, size));
      this.cargo.setMatrixAt(at, matrix);
      this.cargo.setColorAt(at, this.cargoColor.setHex(GRADE_COLORS[0]));
      at++;
    }
    return at;
  }

  /**
   * Where a square's machine draws what it is holding.
   *
   * The same offset the holdings box uses, so an ingot going in lands on the
   * box rather than beside it. `CargoMotion` asks for this because a machine's
   * banks and oven are one box on screen — there are no slots to remember.
   */
  private handsAt = (square: number): IngotSpot => ({
    square,
    x: -HOLDING_OFFSET,
    y: 0.2,
    z: HOLDING_OFFSET
  });

  dispose(): void {
    for (const item of this.disposables) item.dispose();
    this.disposables = [];
    // The belt geometries this view made. Not in `disposables` because a pack
    // swap frees them long before teardown; see `setBeltGeometries`.
    for (const geometry of this.ownedBelts) geometry.dispose();
    this.ownedBelts = [];
  }
}

